import { useState } from "react";
import styled from "@emotion/styled";

import Button from "components/ui/button/Button";
import Drawer from "components/ui/drawer/Drawer";
import { EventData, TrackingProvider } from "lib/tracking";
import { Flex, Text } from "../../../styles/utility-components";
import { CreateLeadFormOutput } from "../create_lead/utils";
import { BulkSaveEntity } from "./bulkSaveEntityConfig";
import { SaveToLead } from "./SaveToLead";

type Props = {
  /** The selection to save — may mix bulk-savable types (e.g. `contact` + `contact_cognism`). */
  entities: BulkSaveEntity[];
  defaultValues: Partial<CreateLeadFormOutput>;
  trackingData: EventData;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  savedLeadIds: string[];
};

/**
 * Slideout drawer for bulk saving multiple entities to a lead. Handles a mixed selection
 * (both bulk-savable types are contacts, so the drawer reads uniformly as "contact").
 */
export function BulkSaveLeadDrawer({
  isOpen,
  trackingData,
  onOpenChange,
  entities,
  defaultValues,
  savedLeadIds,
}: Props) {
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange} width={486} padding={24}>
      <TrackingProvider data={{ "Data type": "CONTACT" }}>
        <DrawerContent aria-label="Bulk save contact drawer">
          <Flex justifyContent="space-between" alignItems="flex-start">
            {isCreateFormOpen ? (
              <Button
                as="button"
                buttonType="secondary"
                icon="arrowNarrowLeft"
                onClick={() => setIsCreateFormOpen(false)}
                aria-label="Go back to Save to a lead"
              />
            ) : (
              <Text h3>Save to a lead</Text>
            )}
            <Button
              as="button"
              buttonType="secondary"
              icon="xClose"
              onClick={() => onOpenChange(false)}
              aria-label="Close drawer"
            />
          </Flex>
          <Flex column height="100%">
            <SaveToLead
              trackingData={trackingData}
              entities={entities}
              defaultValues={defaultValues}
              isCreateFormOpen={isCreateFormOpen}
              onToggleCreateForm={setIsCreateFormOpen}
              savedLeadIds={savedLeadIds}
            />
          </Flex>
        </DrawerContent>
      </TrackingProvider>
    </Drawer>
  );
}

const DrawerContent = styled(Flex)({
  flexDirection: "column",
  height: "100%",
  gap: 24,
  maxWidth: "440px",
});
